import { Injectable, Injector, afterNextRender, computed, effect, inject, signal } from '@angular/core';
import { NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EthelPageContextService } from '../context/ethel-page-context.service';
import { EthelAccessStore } from '../store/ethel-access.store';

/**
 * ADO 149676 (U6 · Ethel Employee Portal Surface), design.md §7.3.
 *
 * Root-provided so it resolves from the SAME injector `app.html` renders `<app-ethel-host />`
 * from — the property to check first if nothing renders.
 *
 * `mounted()` is a conjunction of two fail-closed terms: `access` starts `'unknown'` and
 * `pageContext.current()` starts `null`, so BOTH must flip positive before anything
 * exists in the DOM. `'unknown'` and `'denied'` both mount nothing — AC-U6-07 asserts
 * both, and the `'unknown'` half is the one an implementation usually gets wrong
 * (render, then retract).
 *
 * `access` is a `computed()` over `EthelAccessStore.employeePortalAccess`, not a second
 * raw signal — the store is already the one place that calls `GET /ethel/context` and
 * reads the flag (`loadContext`), so a second copy here would be exactly the
 * duplicated-signal failure class this repo keeps re-shipping (`DRY`).
 *
 * Injects `EthelAccessStore`, NOT the full `EthelStore` (bundle-split follow-up to D-7,
 * gate-decisions.md) — this service is eager (constructed on every page), so anything it
 * imports rides into the initial bundle regardless of `@defer` on the drawer template.
 * `EthelAccessStore` holds only the three things this service touches; the conversation
 * itself (`EthelStore`) is reachable only from the deferred drawer subtree.
 */
@Injectable({ providedIn: 'root' })
export class EthelHostService {
  private readonly pageContext = inject(EthelPageContextService);
  private readonly store = inject(EthelAccessStore);
  private readonly router = inject(Router);
  private readonly injector = inject(Injector);

  readonly access = computed(() => this.store.employeePortalAccess());

  readonly mounted = computed(() => this.access() === 'allowed' && this.pageContext.current() !== null);

  /**
   * Bug fix (149676 U6 follow-up) — true while a route navigation is resolving.
   *
   * A same-app-shell navigation between two Ethel-bearing pages (e.g. Home -> Policies)
   * produces a TRANSIENT `pageContext.current() === null`: the outgoing page's
   * `ngOnDestroy` runs `clearIfOwned` before the incoming page's `ngOnInit` runs `set`,
   * and the two are separated by at least one microtask (Router navigation is always
   * async, even with no guards/resolvers). Without this gate, the close-effect below saw
   * that transient `null` and force-closed the drawer on every ordinary page change —
   * this bug. Navigating to a genuine disclosure surface (EDGE-E8/AC-024) produces the
   * SAME transient `null`, except nothing ever re-publishes, so gating the close on "no
   * navigation is in flight" resolves the ambiguity at the one deterministic checkpoint
   * (the navigation settling) instead of guessing with a timeout — the fragile approach
   * this deliberately avoids. Tracks all four terminal Router events (not just
   * `NavigationEnd`, unlike `App.isNavigating`) because a cancelled/errored navigation
   * that never flips this back to `false` would silently disable the close-on-suppress
   * behaviour for the rest of the session.
   *
   * ROUND 2 — the terminal event is NOT itself the checkpoint. Of the four publishers, three
   * publish from a component CONSTRUCTOR `effect()` (`home-page`, `policy-library-page`,
   * `compliance-view`) and one from `ngOnInit` (`faq-page.component.ts:29`) — both flush during
   * the same CD pass, so the timing below holds for all four. `ApplicationRef.synchronizeOnce()`
   * flushes effects and view CD to quiescence only AFTER the router has emitted `NavigationEnd`.
   * Releasing the gate synchronously on that event therefore released it while
   * `current()` was still `null` (measured live: `closeDrawer()` at NavigationEnd+6ms, the
   * incoming publish at +39ms) — the gate moved the close from "during the gap" to "at
   * `NavigationEnd`", still inside the gap. The release is now scheduled through
   * `afterNextRender`, which `AfterRenderImpl.register` guarantees will run — but only on its
   * `view === undefined` branch, the one that calls `scheduler.notify(RenderHook)` and so ticks
   * even for a `NavigationCancel` that dirties nothing. That branch is taken because
   * `inject(Injector)` in a ROOT-provided service resolves the EnvironmentInjector; handed a
   * component's NodeInjector instead, `register` would push to `view[AFTER_RENDER_SEQUENCES_TO_ADD]`
   * and only `markAncestorsForTraversal`, which is not the same guarantee. Do not "tidy" this
   * into a component injector. `synchronizeOnce` runs render hooks strictly AFTER the effect flush —
   * i.e. after the incoming page has had its chance to publish. Deterministic, not a
   * `setTimeout` guess.
   */
  private readonly navigating = signal(false);

  /** Id of the most recent `NavigationStart`. A guard redirect produces
   *  `Start(1) → Cancel(1) → Start(2)` with no render in between, so the hook queued by
   *  `Cancel(1)` must not release a gate that `Start(2)` has already re-raised. */
  private latestNavigationId = 0;

  constructor() {
    this.router.events.pipe(takeUntilDestroyed()).subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.latestNavigationId = event.id;
        this.navigating.set(true);
      } else if (
        event instanceof NavigationEnd ||
        event instanceof NavigationCancel ||
        event instanceof NavigationError
      ) {
        const settledId = event.id;
        afterNextRender(
          () => {
            if (settledId === this.latestNavigationId) this.navigating.set(false);
          },
          { injector: this.injector },
        );
      }
    });

    // The context call is made once per published portalPage (§7.3) — EthelAccessStore.loadContext
    // caches per page, so re-publishing the same page (e.g. flipping tabs back and forth)
    // never re-fetches.
    effect(() => {
      const ctx = this.pageContext.current();
      if (ctx) this.store.loadContext(ctx.portalPage);
    });

    // EDGE-E8 — suppression CLOSES the store, it does not merely hide it. Deliberately
    // owned HERE (a service that lives for the app's lifetime) rather than inside
    // `EthelHostComponent`: that component is itself destroyed by the very `@if
    // (ethelHost.mounted())` this effect reacts to, so an effect living inside it would
    // race its own teardown. A root-provided singleton has no such race.
    //
    // Gated on `navigating` (see its own doc comment) so an in-flight cross-page
    // navigation's transient unmount doesn't force-close the drawer — only a settled
    // `!mounted()` does (access revoked, or a real disclosure/suppressed surface).
    effect(() => {
      if (this.navigating()) return;
      if (!this.mounted()) {
        this.store.closeDrawer();
      }
    });
  }
}
